import { Injectable, inject } from '@angular/core';
import { DavesaNavigationItem } from '@davesa/components/navigation/navigation.types';
import { UserRoleEnum } from 'app/core/enum/userRole.enum';
import { AuthDavesaV2Service } from 'app/core/auth-davesa/auth-davesa-v2.service';
import { Observable, from, map, of, catchError } from 'rxjs';
import { getUserRoleName } from '../utils/user-utils';

@Injectable({
    providedIn: 'root'
})
export class MenuPermissionService {
    private _authDavesaV2Service = inject(AuthDavesaV2Service);

    /**
     * Check if user has permission to access a menu item
     */
    hasMenuPermission(item: DavesaNavigationItem): Observable<boolean> {
        return from(this._authDavesaV2Service.getLogInUserDetails()).pipe(
            map((user: any) => {
                // If no permission defined, allow access
                if (!item.moduleAccessPermission && !item.accessCondition) {
                    return true;
                }

                // Check moduleAccessPermission (role-based)
                if (item.moduleAccessPermission && Array.isArray(item.moduleAccessPermission)) {
                    const userRole = this.getUserRole(user);
                    if (userRole && item.moduleAccessPermission.includes(userRole)) {
                        return true;
                    }
                }

                // Check accessCondition (more flexible)
                if (item.accessCondition) {
                    const userRole = this.getUserRole(user);
                    // If accessCondition is array of roles
                    if (Array.isArray(item.accessCondition)) {
                        if (item.accessCondition[0] === '*') {
                            return true; // Allow all
                        }
                        return item.accessCondition.includes(userRole);
                    }
                    // If accessCondition is a function
                    if (typeof item.accessCondition === 'function') {
                        return item.accessCondition(user);
                    }
                }
                return false;
            })
        );
    }

    /**
     * Get user role from user data (handle multiple data structures)
     * Priority: Use the currently logged in role, not all available roles
     */
    private getUserRole(user: any): string | null {
        if (!user) return null;
        
        // PRIORITY 1: Get the current login role from the auth service
        try {
            const currentLoginUser = this._authDavesaV2Service.loginUser();
            const roleName = getUserRoleName(currentLoginUser);
            if (roleName) {
                return roleName;
            }
        } catch (error) {
        }
        
        // PRIORITY 2: Try user.user_role.name (from loginUser data passed in)
        if (user.user_role?.name) {
            return user.user_role.name.toLowerCase();
        }
        
        // PRIORITY 3: user.user_role.role.name (old structure)
        if (user.user_role?.role?.name) {
            return user.user_role.role.name.toLowerCase();
        }
        
        // LOWEST PRIORITY: user.user_roles[0].role.name (all roles - not reliable for current session)
        if (user.user_roles && Array.isArray(user.user_roles) && user.user_roles.length > 0) {
            const firstRole = user.user_roles[0];
            if (firstRole.role?.name) {
                return firstRole.role.name.toLowerCase();
            }
        }
        
        return null;
    }

    /**
     * Filter navigation items based on permissions
     */
    filterNavigationByPermission(navigation: DavesaNavigationItem[]): Observable<DavesaNavigationItem[]> {
        return from(this._authDavesaV2Service.getLogInUserDetails()).pipe(
            map((user: any) => {
                const userRole = this.getUserRole(user);
                
                // If user is null or undefined, return empty navigation (no access)
                if (!user) {
                    return [];
                }
                
                if (!userRole) {
                    return [];
                }
                
                const filtered = this.filterItemsRecursively(navigation, user);
                return filtered;
            }),
            catchError(error => {
                // If there's an error getting user data, return empty navigation (secure fallback)
                return of([]);
            })
        );
    }

    /**
     * Recursively filter navigation items and their children
     */
    private filterItemsRecursively(items: DavesaNavigationItem[], user: any): DavesaNavigationItem[] {
        return items.filter(item => {
            // Check if user has permission for this item
            const hasPermission = this.checkItemPermission(item, user);
            
            if (!hasPermission) {
                return false;
            }

            // If item has children, filter them recursively
            if (item.children && item.children.length > 0) {
                const originalChildrenCount = item.children.length;
                const filteredChildren = this.filterItemsRecursively(item.children, user);
                
                // Update the item with filtered children
                item.children = filteredChildren;
                
                // If it's a group or collapsable item with no visible children, hide it
                if ((item.type === 'group' || item.type === 'collapsable') && filteredChildren.length === 0) {
                    return false;
                }
            }

            return true;
        });
    }

    /**
     * Check permission for individual item
     */
    private checkItemPermission(item: DavesaNavigationItem, user: any): boolean {
        // If no permission defined, allow access
        if (!item.moduleAccessPermission && !item.accessCondition) {
            return true;
        }

        // If no user, deny access
        if (!user) {
            return false;
        }

        const userRole = this.getUserRole(user);

        // If no user role, deny access
        if (!userRole) {
            return false;
        }

        // Check moduleAccessPermission (role-based)
        if (item.moduleAccessPermission && Array.isArray(item.moduleAccessPermission)) {
            
            if (userRole && item.moduleAccessPermission.includes(userRole)) {
                return true;
            }
        }

        // Check accessCondition (more flexible)
        if (item.accessCondition) {
            // If accessCondition is array of roles
            if (Array.isArray(item.accessCondition)) {
                if (item.accessCondition[0] === '*') {
                    return true; // Allow all
                }
                if (item.accessCondition.includes(userRole)) {
                    return true;
                }
            }
            
            // If accessCondition is a function
            if (typeof item.accessCondition === 'function') {
                return item.accessCondition(user);
            }
        }

        return false;
    }

    /**
     * Get user info with better error handling
     */
    getUserInfo(): Observable<any> {
        return from(this._authDavesaV2Service.getLogInUserDetails()).pipe(
            map(user => {
                return user;
            }),
            catchError(error => {
                return of(null);
            })
        );
    }

    /**
     * Check if user has specific role
     */
    hasRole(requiredRoles: string | string[]): Observable<boolean> {
        return from(this._authDavesaV2Service.getLogInUserDetails()).pipe(
            map((user: any) => {
                const userRole = this.getUserRole(user);
                
                if (!userRole) {
                    return false;
                }

                if (typeof requiredRoles === 'string') {
                    return userRole === requiredRoles.toLowerCase();
                }

                if (Array.isArray(requiredRoles)) {
                    return requiredRoles.some(role => userRole === role.toLowerCase());
                }

                return false;
            })
        );
    }

    /**
     * Check if user is admin
     */
    isAdmin(): Observable<boolean> {
        return this.hasRole([UserRoleEnum.admin, UserRoleEnum.superAdmin]);
    }

    /**
     * Check if user is site manager or admin
     */
    isSiteManagerOrAdmin(): Observable<boolean> {
        return this.hasRole([UserRoleEnum.admin, UserRoleEnum.siteManager, UserRoleEnum.superAdmin]);
    }
} 